import React, { useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import ValidationLayout from '../components/AdminValidations';
import Ports from '../components/AdminValidations/Ports';
import { ErrorMessages, TableConstants } from '../utils/appConstants';
import { SortOrder } from '../enums';
import ValidationService from '../services/validations.service';
import { VerifyPopup } from '../components/AdminValidations/Ports/VerifyPopup';
import { ErrorCodeMessageMapping, ErrorCodesMapping, ErrorMessages as Errors } from '../utils/apiConstants';
import ErrorInfoIcon from '../assets/images/error-info-icon.svg';

const PortVerification: React.FC = () => {
  const [verified, setVerified] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [selected, setSelected] = useState<any[]>([]);
  const [verifyAllLoading, setVerifyAllLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [ports, setPorts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [order, setOrder] = useState<SortOrder>(SortOrder.asc);
  const [orderBy, setOrderBy] = useState<string>('id');
  const [verifyLoadingId, setVerifyLoadingId] = useState<string>('');
  const [verifyPopupOpen, setVerifyPopupOpen] = useState<boolean>(false);
  const [neAvailablePorts, setNeAvailablePorts] = useState<number[]>([]);
  const [neTotalPorts, setNeTotalPorts] = useState<number>(0);
  const [neName, setNeName] = useState<string>('');
  const [neManagementIp, setNeManagementIp] = useState<string>('');
  const isComponentMounted = React.useRef(true);
  React.useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  const fetchPorts = useCallback(() => {
    setLoading(true);

    const payload = {
      verified,
      sortField: orderBy,
      sortDirection: order.toUpperCase(),
      page,
      pageSize: TableConstants.perPage,
      searchText,
    };

    ValidationService.getPorts(payload)
      .then((response: any) => {
        if (!isComponentMounted.current) return;
        setLoading(false);
        setPorts(response?.data ?? []);
        setTotal(response?.pagination?.totalRecords ?? 0);
      })
      .catch(() => {
        if (!isComponentMounted.current) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setLoading(false);
        setPorts([]);
        setTotal(0);
      });
  }, [setLoading, setPorts, setTotal, verified, orderBy, order, page, searchText]);

  const handleRequestSort = useCallback(
    (property: string, nextSortOrder: SortOrder) => {
      setOrder(nextSortOrder);
      setOrderBy(property);
    },
    [orderBy, order],
  );

  const handleSearchTextChanged = useCallback((searchText: string) => {
    setSearchText(searchText);
    setPage(1);
    setSelected([]);
  }, [setSearchText, setPage, setSelected]);

  const handleRowSelect = useCallback((newSelected: any[]) => {
    setSelected(newSelected);
  }, [setSelected, selected]);

  const handleVerifiedChange = useCallback((checked: boolean) => {
    setVerified(checked);
    setOrderBy('id');
    setPage(1);
  }, [setVerified]);

  // const handleVerifyAllClick = useCallback(() => {
  //   setVerifyAllLoading(true);
  //   const payload = {
  //     nrs: selected.map((item) => item.id),
  //   };

  //   ValidationService.validatePort(payload)
  //     .then(() => {
  //       if (selected.length === ports.length && page !== 1) {
  //         setPage(1);
  //       } else {
  //         fetchPorts();
  //       }
  //       setVerifyAllLoading(false);
  //       setSelected([]);
  //     })
  //     .catch(() => {
  //       setVerifyAllLoading(false);
  //       setSelected([]);
  //     });
  // }, [ports, selected, page, setSelected, fetchPorts, setVerifyAllLoading]);

  // HERE
  const handleVerifyClick = useCallback((id: string) => {
    setVerifyLoadingId(id);

    const payload = {
      portIds: [id],
    };

    ValidationService.getAvailablePortsOnSwitch(payload)
      .then((response: any) => {
        if (!isComponentMounted.current) return;
        const valid_positions: number[] = [];
        const positions = response?.data[0] ?? [];
        positions.forEach((position:any) => {
          valid_positions.push(position.position_in_ne);
        });
        const ne_details = response?.data[1] ?? [];
        setNeManagementIp(ne_details.management_ip);
        setNeName(ne_details.ne_name);
        setNeTotalPorts(ne_details.ne_available_port);
        setNeAvailablePorts(valid_positions);
        setVerifyPopupOpen(true);
      })
      .catch((e:any) => {
        if (!isComponentMounted.current) return;
        if (e.errorCode === ErrorCodesMapping[1015]) {
          toast.error(ErrorCodeMessageMapping[1015], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
        setNeAvailablePorts([]);
        setVerifyLoadingId('');
        setNeManagementIp('');
        setNeName('');
        setNeTotalPorts(0);
        setVerifyPopupOpen(false);
      });
  }, [setVerifyLoadingId, setVerifyPopupOpen, setNeAvailablePorts]);

  const handleVerifyClickInPopup = useCallback((id: string, positionInNe: number) => {
    const payload = {
      nrs: [id],
      position: positionInNe,
    };

    ValidationService.validatePort(payload)
      .then(() => {
        if (!isComponentMounted.current) return;
        setVerifyLoadingId('');
        if (ports.length > 1) {
          fetchPorts();
        } else if (page !== 1) {
          setPage(1);
        } else {
          fetchPorts();
        }
      })
      .catch((e:any) => {
        if (!isComponentMounted.current) return;
        if (e.errorCode === ErrorCodesMapping[1015]) {
          toast.error(ErrorCodeMessageMapping[1015], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
        setVerifyLoadingId('');
      });
    setSelected([]);
  }, [setVerifyLoadingId, fetchPorts, setSelected, setPage, ports, verified, page, orderBy, order, searchText]);

  const handleMoveClickInPopup = useCallback((id: string, positionInNe: number) => {
    const payload = {
      nrs: [id],
      position: positionInNe,
    };

    ValidationService.movePort(payload)
      .then(() => {
        if (!isComponentMounted.current) return;
        setVerifyLoadingId('');
        if (ports.length > 1) {
          fetchPorts();
        } else if (page !== 1) {
          setPage(1);
        } else {
          fetchPorts();
        }
      })
      .catch((e:any) => {
        if (!isComponentMounted.current) return;
        if (e.errorCode === ErrorCodesMapping[1015]) {
          toast.error(ErrorCodeMessageMapping[1015], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        } else if (e.errorCode === ErrorCodesMapping[1016]) {
          toast.error(ErrorCodeMessageMapping[1016], { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        } else {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        }
        setVerifyLoadingId('');
      });
    setSelected([]);
  }, [setVerifyLoadingId, fetchPorts, setSelected, setPage, ports, verified, page, orderBy, order, searchText]);

  const handleSelectAll = useCallback(() => {
    if (selected.length !== ports.length) {
      const newSelected = ports.map((port) => {
        return { id: port.id };
      });
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  }, [ports, selected, setSelected]);

  const handlePageChange = useCallback((page: number) => {
    setPage(page);
    setSelected([]);
  }, [setPage, setSelected]);

  useEffect(() => {
    fetchPorts();
  }, [page, orderBy, verified, order, searchText, fetchPorts]);

  return (
    <>
      <ValidationLayout
        title="Physical Validation - Port"
        table={<Ports page={page} verified={verified} total={total} ports={ports} loading={loading} verifyLoadingId={verifyLoadingId} order={order} orderBy={orderBy} onPageChange={handlePageChange} selected={selected} onSelectRow={handleRowSelect} onSelectAll={handleSelectAll} onVerifyClick={handleVerifyClick} onRequestSort={handleRequestSort} />}
        verifyAllLoading={verifyAllLoading}
        selected={selected}
        verified={verified}
        onVerifiedChange={handleVerifiedChange}
        onSearchChanges={handleSearchTextChanged}
        // onVerifyAllClick={handleVerifyAllClick}
        isVerifiedSection
        ports
        isSearchSection
      />
      {verifyPopupOpen && (
        <VerifyPopup
          verified={verified}
          portId={verifyLoadingId}
          setVerifyLoadingId={setVerifyLoadingId}
          positions_in_ne={neAvailablePorts}
          verifyButton={handleVerifyClickInPopup}
          moveButton={handleMoveClickInPopup}
          setVerifyPopupOpen={setVerifyPopupOpen}
          ne_available_port={neTotalPorts}
          ne_management_ip={neManagementIp}
          ne_name={neName}
        />
      )}
    </>
  );
};

export default PortVerification;
